import type {
  CoordinateCalibration,
  OutpostState,
  PlayerState,
  QuestState,
  ResourceRequirement,
  RoutePlan,
  RouteStop
} from '../../types/game'

interface RouteInput {
  player: PlayerState
  quests: QuestState[]
  outposts: OutpostState[]
  resources: ResourceRequirement[]
  calibrations: CoordinateCalibration[]
}
function questPriority(quest: QuestState, player: PlayerState): number {
  let score = 40
  if (quest.objectivesMet) score += 50
  if (quest.targetBiome === player.biome) score += 25
  if (quest.targetOutpostId !== null) score += 15
  if (quest.unlocks.length > 0) score += 8
  if (quest.id === 53) score += 12
  return score
}

export function buildRoutePlan(input: RouteInput): RoutePlan {
  const outpostById = new Map(input.outposts.map((outpost) => [outpost.id, outpost]))
  const stops: RouteStop[] = [{
    order: 1,
    kind: 'current',
    title: '当前位置',
    detail: `地区 ${input.player.biome} · ${input.player.x.toFixed(1)}, ${input.player.y.toFixed(1)}`,
    questId: null,
    biome: input.player.biome,
    outpostId: null,
    worldX: input.player.x,
    worldY: input.player.y,
    priority: 999,
    evidence: 'direct_save_evidence'
  }]

  const questStops = input.quests
    .map((quest): RouteStop => {
      const outpost = quest.targetOutpostId === null
        ? null
        : outpostById.get(quest.targetOutpostId) ?? null
      const missingObjectives = quest.objectives.filter((objective) => !objective.completed)
      const kind = quest.objectivesMet ? 'turn-in' : quest.targetOutpostId !== null ? 'quest' : 'collect'
      const detail = quest.objectivesMet
        ? '目标已满足，优先尝试交付'
        : missingObjectives.map((objective) => (
          `${objective.targetName} ${objective.current}/${objective.required}`
        )).join('；') || '继续推进任务目标'

      return {
        order: 0,
        kind,
        title: quest.title,
        detail,
        questId: quest.id,
        biome: quest.targetBiome,
        outpostId: quest.targetOutpostId,
        worldX: outpost?.estimatedWorldX ?? null,
        worldY: outpost?.estimatedWorldY ?? null,
        priority: questPriority(quest, input.player),
        evidence: outpost?.evidence ?? quest.evidence
      }
    })
    .sort((left, right) => right.priority - left.priority)

  questStops.forEach((stop, index) => {
    stop.order = index + 2
    stops.push(stop)
  })

  const currentCalibration = input.calibrations.find(
    (calibration) => calibration.biome === input.player.biome
  )
  const notes = [
    '当前路线按任务完成度、当前地区、据点目标和后续解锁进行语义排序。',
    '没有可靠世界坐标的任务只提供访问顺序，不绘制伪精确路线。'
  ]
  if (!currentCalibration || currentCalibration.status === 'unverified') {
    notes.push('世界坐标到地图像素的变换尚未校准。')
  }

  return {
    generatedAt: new Date().toISOString(),
    mode: 'semantic-priority',
    coordinateStatus: currentCalibration?.status ?? 'unverified',
    stops,
    notes
  }
}
